/**
 * Guided-tour definitions. Each tour is an array of Joyride Step objects targeting
 * live `data-testid` selectors. Kicked off from TrainingPage.js — navigation to the
 * right route happens in GuidedTourContext before the tour starts.
 */

// Top 3 tours chosen by activation value:
//   1. getting-started  — orients brand-new users on the Dashboard
//   2. tasks             — core workflow activation
//   3. ontology          — flagship recent feature

export const TOURS = {
  'getting-started': {
    id: 'getting-started',
    label: 'Getting Started Tour',
    route: '/dashboard',
    steps: [
      {
        target: '[data-testid="dashboard-page"]',
        title: 'Welcome to KStreak',
        content: "This is your Dashboard — your daily starting point. Everything important (tasks, knowledge health, and upcoming webinars) surfaces here.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="quick-actions"]',
        title: 'Quick Actions',
        content: "Jump straight to creating an Article, Task, or Webinar — no digging through menus.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="dashboard-metrics"]',
        title: 'Key Metrics',
        content: "At-a-glance stats on task completion, knowledge health, overdue items, and anomalies. Click any card to drill down.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="upcoming-webinars-widget"]',
        title: 'Upcoming Webinars',
        content: "Your next scheduled webinars — one-click join or open the detail page for recordings and transcripts.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="recent-activity-widget"]',
        title: 'Recent Activity',
        content: "A live feed of what your team is doing — articles created, tasks completed, decisions logged.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="whats-new-trigger"]',
        title: "What's New",
        content: "Click the sparkles icon anytime to see the latest features, improvements, and security updates shipped.",
        placement: 'bottom-end',
        disableBeacon: true,
      },
    ],
  },

  'task-workflow': {
    id: 'task-workflow',
    label: 'Task Workflow Tour',
    route: '/tasks',
    steps: [
      {
        target: '[data-testid="tasks-page"]',
        title: 'Task Workflow Engine',
        content: "Tasks in KStreak follow a clear 5-status workflow: To Do → In Progress → Review → Done. 'Blocked' is a side status for stuck items.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="create-task-btn"]',
        title: 'Create a Task',
        content: "Click here to create a new task with title, priority, due date, assignee, dependencies, and attachments.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-tasks"]',
        title: 'List View',
        content: "Default table view with search, priority/status/team filters, bulk actions, and CSV/PDF export.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-kanban"]',
        title: 'Kanban Board',
        content: "Switch to a drag-and-drop Kanban board with 5 colour-coded columns and swimlanes by assignee or priority.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-workflow"]',
        title: 'Workflow Report',
        content: "Auto-generated report showing overdue, blocked, and at-risk tasks — plus GPT-5.2 predicted completion rates per assignee.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="task-search-input"]',
        title: 'Search & Filter',
        content: "Type to search by title or description. Combine with priority, status, and team filters to narrow down in seconds.",
        disableBeacon: true,
      },
    ],
  },

  ontology: {
    id: 'ontology',
    label: 'Ontology Engine Tour',
    route: '/ontology',
    steps: [
      {
        target: '[data-testid="ontology-tabs"]',
        title: 'Welcome to the Ontology Engine',
        content: "This is where you map out your organisation's knowledge taxonomy — classes, relations, individuals, and triples. All 8 tabs work together.",
        placement: 'bottom',
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-classes"]',
        title: 'Classes — The Vocabulary',
        content: "Define the concepts in your organisation (Methodology, Team, Customer Segment). Nest them for 'is-a' hierarchies.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-relations"]',
        title: 'Relations — The Verbs',
        content: "Typed relationships like 'implements', 'manages', 'depends-on'. Set domain + range to keep the graph semantically clean.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-individuals"]',
        title: 'Individuals — The Instances',
        content: "Real examples of classes ('KCS v6' is an instance of 'Methodology'). Link them to PKM notes for grounded knowledge.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-graph"]',
        title: 'Graph Visualisation',
        content: "Navigate the full ontology as an interactive ReactFlow canvas. Toggle individuals on/off for different perspectives.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-search"]',
        title: 'Semantic Search (GPT-5.2)',
        content: "Ask natural-language questions — GPT-5.2 translates them into structured ontology queries, explains the plan, and returns grounded individuals.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-diff"]',
        title: 'Time-Machine Diff Viewer',
        content: "Pick any two points in time → see everything added, removed, renamed, or migrated, with GPT-5.2 governance commentary.",
        disableBeacon: true,
      },
    ],
  },

  kanban: {
    id: 'kanban',
    label: 'Kanban Board Tour',
    route: '/tasks',
    steps: [
      {
        target: '[data-testid="tab-kanban"]',
        title: 'Open the Kanban Board',
        content: "Click here to switch from the List view to a drag-and-drop Kanban board.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tasks-page"]',
        title: '5 Colour-coded Columns',
        content: "To Do · In Progress · Review · Blocked · Done. Cards show priority chips, assignee avatars, and due dates at a glance.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="tasks-page"]',
        title: 'Drag-and-Drop',
        content: "Grab a card and drop it in a new column to change status. Done/Blocked transitions prompt you for a summary or reason — workflow discipline enforced.",
        placement: 'center',
        disableBeacon: true,
      },
    ],
  },

  'doc-review': {
    id: 'doc-review',
    label: 'Document Review Tour',
    route: '/articles',
    steps: [
      {
        target: '[data-testid="articles-page"]',
        title: 'Article Knowledge Base',
        content: "Articles are your team's living documentation. Create, review, annotate, and publish — with versioning + comments throughout.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="article-search"]',
        title: 'Full-text Search',
        content: "Search across title, content, and annotations. Combine with category + team filters to narrow down fast.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="article-category-select"]',
        title: 'Filter by Category',
        content: "Articles are organised by category. Use this to focus on a single knowledge domain.",
        disableBeacon: true,
      },
    ],
  },

  'workflow-report': {
    id: 'workflow-report',
    label: 'Workflow Report Tour',
    route: '/tasks',
    steps: [
      {
        target: '[data-testid="tab-workflow"]',
        title: 'Workflow Report',
        content: "Auto-generated operational report — what's at risk, what's overdue, and GPT-5.2 predictions for completion rates.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="at-risk-section"]',
        title: 'At-Risk Tasks',
        content: "Tasks stuck >5 days in progress or >2 days blocked are flagged here. One-click escalation generates a follow-up task.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="blocked-section"]',
        title: 'Blocked Tasks',
        content: "All blocked tasks with blocker reasons + the person they're waiting on. Clears fast once you see the bottleneck.",
        disableBeacon: true,
      },
    ],
  },

  reports: {
    id: 'reports',
    label: 'Predictive Analytics Tour',
    route: '/analytics',
    steps: [
      {
        target: 'body',
        title: 'Predictive Analytics Dashboard',
        content: "GPT-5.2-authored reports on knowledge health, team capability, and knowledge gap predictions. Refreshed nightly.",
        placement: 'center',
        disableBeacon: true,
      },
    ],
  },

  'knowledge-maps': {
    id: 'knowledge-maps',
    label: 'Knowledge Maps Tour',
    route: '/knowledge-maps',
    steps: [
      {
        target: '[data-testid="create-map-btn"]',
        title: 'Create a Knowledge Map',
        content: "Pick a topic → KStreak auto-generates a force-directed graph of related articles, notes, tasks, and people. Node size = PageRank.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="analyze-btn"]',
        title: 'Anomaly Detection',
        content: "Surfaces orphaned concepts, silos, and clusters with unusual connection density — powered by GPT-5.2.",
        disableBeacon: true,
      },
    ],
  },

  pkm: {
    id: 'pkm',
    label: 'PKM & Decision Advisor Tour',
    route: '/pkm',
    steps: [
      {
        target: 'body',
        title: 'Personal Knowledge Management',
        content: "Your versioned second brain — every edit creates a new version, and notes link to each other with typed relationships.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="add-relationship-btn"]',
        title: 'Link Relationships',
        content: "Connect notes with typed relationships (supports, contradicts, extends) to build your personal knowledge graph.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="advisor-panel"]',
        title: 'Decision Advisor',
        content: "Describe a decision you're facing — the AI analyses your notes + relationships to provide structured pros/cons + confidence.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="ontology-assist-panel"]',
        title: 'Ontology Assist (GPT-5.2)',
        content: "Click 'Analyze with AI' to get class suggestions, identified individuals, and proposed new ones — with one-click apply/link/create.",
        disableBeacon: true,
      },
    ],
  },

  webinars: {
    id: 'webinars',
    label: 'Webinars & Recording Intelligence Tour',
    route: '/webinars',
    steps: [
      {
        target: 'body',
        title: 'Webinars Hub',
        content: "Schedule, host, record, and analyse webinars. Every recording gets auto-chapters, transcripts, and breakout detection.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="add-recording-btn"]',
        title: 'Upload / Attach Recording',
        content: "Attach an existing recording — KStreak transcribes via OpenAI Whisper and auto-generates chapters.",
        disableBeacon: true,
      },
    ],
  },

  recurring: {
    id: 'recurring',
    label: 'Calendar & Recurring Tasks Tour',
    route: '/calendar',
    steps: [
      {
        target: '[data-testid="calendar-page"]',
        title: 'Unified Calendar',
        content: "Tasks (by due date), webinars, and audit events in one colour-coded view.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="month-view-tab"]',
        title: 'Month / Week / Day',
        content: "Toggle between views. Click any event to jump straight to the source module.",
        disableBeacon: true,
      },
    ],
  },

  'voice-journal': {
    id: 'voice-journal',
    label: 'Voice Journal Tour',
    route: '/voice-journal',
    steps: [
      {
        target: '[data-testid="voice-journal-page"]',
        title: 'Voice Journal',
        content: "Record decisions, retrospectives, and customer calls — KStreak transcribes them via OpenAI Whisper and indexes for full-text search.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="record-btn"]',
        title: 'Start Recording',
        content: "Click to start. Audio is recorded locally at high quality before upload — no browser-quality compression.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="auto-create-note-toggle"]',
        title: 'Auto-create PKM Note',
        content: "Toggle this and KStreak automatically creates a PKM note from the transcript + GPT-5.2 summary.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="auto-create-tasks-toggle"]',
        title: 'Auto-create Tasks',
        content: "KStreak can also extract action items from the transcript and drop them straight into your task list.",
        disableBeacon: true,
      },
    ],
  },

  'ask-assistant': {
    id: 'ask-assistant',
    label: 'Ask Assistant Tour',
    route: '/dashboard',
    steps: [
      {
        target: 'body',
        title: 'Global AI Assistant',
        content: "Look for the sparkles icon or the ⌘K shortcut — Ask Assistant retrieves grounded answers from PKM, Articles, Voice Journal, Ontology, and Chat.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="whats-new-trigger"]',
        title: 'Global Search Bar',
        content: "Hit ⌘K anywhere to open Ask Assistant. Ask in natural language — every answer cites the source note/article/chat.",
        disableBeacon: true,
      },
    ],
  },

  'knowledge-transfer': {
    id: 'knowledge-transfer',
    label: 'Knowledge Transfer Tour',
    route: '/knowledge-transfer',
    steps: [
      {
        target: '[data-testid="knowledge-transfer-page"]',
        title: 'Knowledge Transfer (KT) Plans',
        content: "Structured handoffs for role changes and offboarding — AI-suggested artefacts, session scheduling, and capability assessment.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="create-transfer-btn"]',
        title: 'Start a KT Plan',
        content: "Pick source + target + timeline. KStreak scans the source's historical activity to recommend what to transfer.",
        disableBeacon: true,
      },
    ],
  },

  'automation-observability': {
    id: 'automation-observability',
    label: 'Automation Observability Tour',
    route: '/admin/automations/runs',
    steps: [
      {
        target: '[data-testid="automation-runs-log-page"]',
        title: 'Automation Runs Dashboard',
        content: "Every scheduled + manual automation run with status, duration, triggered-by, and filters. Your ops observability layer.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="filter-automation"]',
        title: 'Filter by Automation',
        content: "Focus on a single automation (readiness watchdog, ontology audit, changelog digest). Combine with status + triggered-by for fast triage.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="filter-since"]',
        title: 'Date Range',
        content: "Last 24h? Last week? Spot trends over time. Pair with the sparkline on /admin/email for quick regression detection.",
        disableBeacon: true,
      },
    ],
  },

  'email-security-admin': {
    id: 'email-security-admin',
    label: 'Email Admin & Security Tour',
    route: '/admin/email',
    steps: [
      {
        target: '[data-testid="email-config-page"]',
        title: 'Email Admin Hub',
        content: "DMARC posture, Spoofer Blocklist, Readiness checks, Domains, Watchdog, and Digest — all in one place.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="dmarc-check-card"]',
        title: 'DMARC Record Check',
        content: "Grades your DMARC policy. Missing record or p=none = spoofable — fix before your first phishing incident.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="dmarc-reports-card"]',
        title: 'DMARC Aggregate Reports',
        content: "Upload DMARC XML/GZ/ZIP reports (or auto-ingest) to see who's sending as you — legitimate infra + likely spoofers.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="dmarc-blocklist-card"]',
        title: 'Spoofer Blocklist Feed',
        content: "Plain-text CIDR feed at /api/email-config/dmarc-blocklist.txt — point Cloudflare/fail2ban/CrowdSec at it for auto-blocking.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="watchdog-card"]',
        title: 'Readiness Watchdog',
        content: "Runs weekly readiness checks and alerts only when pass rate drops below threshold — zero-noise ops alerting.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="digest-card"]',
        title: 'Automation Health Digest',
        content: "Monday-morning email + Slack with regressions, duration outliers, and per-automation scoreboard from last 7 days.",
        disableBeacon: true,
      },
    ],
  },

  'knowledge-audit': {
    id: 'knowledge-audit',
    label: 'Knowledge Audit & Governance Tour',
    route: '/knowledge-audit',
    steps: [
      {
        target: 'body',
        title: 'Knowledge Audit & Governance',
        content: "Tamper-evident log of every create/edit/delete across PKM, Articles, Tasks, Ontology, and Decisions. SOC2/GDPR-ready.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="export-csv-btn"]',
        title: 'Export for Compliance',
        content: "Filter by module/actor/date and export signed CSV, JSON, or PDF. Drop straight into your SOC2 evidence binder.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="alert-config-card"]',
        title: 'Tamper Alerts',
        content: "Configure automatic alerts when any row's audit hash breaks — instant notification of log tampering attempts.",
        disableBeacon: true,
      },
    ],
  },

  chat: {
    id: 'chat',
    label: 'Team Chat & Presence Tour',
    route: '/chat',
    steps: [
      {
        target: '[data-testid="chat-page"]',
        title: 'Team Chat & Presence',
        content: "Real-time DMs, channels, and @mentions — two-pane layout with live presence dots for every teammate.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="chat-search-input"]',
        title: 'Find a Teammate',
        content: "Search teammates + channels. Click any name to instantly start a 1:1 DM — KStreak auto-creates a deterministic room so both sides resolve to the same conversation.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="room-workspace"]',
        title: 'Channels & Unread Badges',
        content: "Every channel + DM shows a red unread badge (capped at 99+). Badges also appear on the Chat nav item in the sidebar — so you never miss a message.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="mute-workspace"]',
        title: 'Mute Noisy Rooms',
        content: "Hover any room → bell icon → 'Room muted'. Per-room mute wins over global notification prefs — you still see badges, just no pushes.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="push-toggle-btn"]',
        title: 'Browser Push Notifications',
        content: "Click this bell button → grant permission → KStreak subscribes via Web Push + VAPID. Get notified on DMs + @mentions even when the tab is closed. Deep-links right back to the conversation.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="chat-main-pane"]',
        title: 'Active Conversation',
        content: "The right pane is your active conversation — send messages, react, @mention, and thread replies. Messages are indexed for full-text search and can be linked from PKM notes.",
        placement: 'center',
        disableBeacon: true,
      },
    ],
  },

  'iso-compliance': {
    id: 'iso-compliance',
    label: 'ISO 30401 Compliance Tour',
    route: '/compliance',
    steps: [
      {
        target: '[data-testid="compliance-page"]',
        title: 'ISO 30401:2018 Compliance Center',
        content: "Your KMS, benchmarked against the only international KM standard. 7 clauses from Context → Improvement, scored end-to-end.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="compliance-rate-badge"]',
        title: 'Overall Compliance Rate',
        content: "At-a-glance % across every clause. Green ≥80% · amber 50-80% · red <50%. Share with your CKO or drop straight into a board deck.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="compliance-dashboard"]',
        title: '6-Clause Scorecard',
        content: "Each clause shows its own status card — instantly see which ISO requirement needs the most work. Click through to the full checklist below.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="auto-evaluate-btn"]',
        title: 'Auto-Evaluate',
        content: "One click → KStreak inspects your live config (PKM notes, ontology, KT workflows, audit trail, training coverage, decisions) and scores every sub-clause automatically.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-checklist"]',
        title: 'Compliance Checklist',
        content: "The full clause-by-clause rubric. Passed clauses show their supporting evidence; gaps show remediation actions you can accept with one click.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="schedule-card"]',
        title: 'Weekly Compliance Digest',
        content: "Arm the schedule → KStreak emails your CKO (or Slack channel) a weekly summary of status changes, auto-eval results, and newly-open gaps. Zero manual tracking.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="tab-risks"]',
        title: 'Risk Register (Clause 6.1)',
        content: "Manage knowledge-related risks with probability, impact, mitigation strategy, and owner — ISO-aligned risk management out of the box.",
        disableBeacon: true,
      },
    ],
  },

  'capability-benchmarking': {
    id: 'capability-benchmarking',
    label: 'Capability Assessment & KM Benchmarking Tour',
    route: '/tests',
    steps: [
      {
        target: '[data-testid="capability-assessment-page"]',
        title: 'Capability Assessment',
        content: "Score your org's KM maturity across People, Process, and Technology. 5-minute form → 4-tier maturity badge → AI recommendations.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="benchmarking-btn"]',
        title: 'Open KM Benchmarking',
        content: "Once you've got a submission, click here — KStreak compares your scores against 32 peer benchmarks (8 industries × 4 company sizes) calibrated from APQC, Gartner, and KM Institute studies.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="benchmarking-industry-select"]',
        title: 'Pick Your Industry',
        content: "8 industries available: Technology, Professional Services, Financial Services, Healthcare, Manufacturing, Government, Retail, Education. Each has calibrated peer norms.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="benchmarking-size-select"]',
        title: 'Pick Your Company Size',
        content: "4 tiers: 1-50, 51-250, 251-1000, 1000+. Enterprise-scale and startup-scale KM look very different — benchmarks reflect that.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="benchmarking-generate-btn"]',
        title: 'Generate Report',
        content: "KStreak computes gap analysis, percentile ranking, and projected KPI uplift across 6 KPIs — all grounded in your peer segment.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="benchmarking-insights-btn"]',
        title: 'AI Insights (GPT-5.2)',
        content: "Click to get a grounded executive summary, root causes for gaps, prioritised recommendations, and a narrative on expected performance impact.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="benchmarking-share-btn"]',
        title: 'Share with Stakeholders',
        content: "Email a branded summary + PDF to your board / exec team. Each recipient gets a unique view-token so you can track who opened (+ how many times) on the Share Activity card.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="benchmarking-pdf-btn"]',
        title: 'Export Branded PDF',
        content: "Multi-page PDF with radar chart, gap table, KPI projections, and AI narrative. Drops straight into board decks or annual reports.",
        disableBeacon: true,
      },
    ],
  },

  'whats-new': {
    id: 'whats-new',
    label: "What's New Tour",
    route: '/whats-new',
    steps: [
      {
        target: '[data-testid="whats-new-page"]',
        title: "What's New Archive",
        content: "Every release shipped in KStreak. Features, improvements, security, fixes — all searchable and categorised.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="whats-new-search"]',
        title: 'Search Releases',
        content: "Full-text search across title, summary, and body markdown. Great for 'did we ship X yet?' checks.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="whats-new-filters"]',
        title: 'Category Filters',
        content: "Filter by New / Improved / Security / Fix / News. Keep up with just what matters to you.",
        disableBeacon: true,
      },
      {
        target: '[data-testid="whats-new-trigger"]',
        title: 'In-app Popover',
        content: "The sparkles icon in the header shows recent releases with a red unread badge. Click the beacon anytime.",
        disableBeacon: true,
      },
    ],
  },
};

export const TOUR_IDS = Object.keys(TOURS);
